import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess, getCrmRole } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      // The cached sale has no avatar, so the identity uses the loaded one.
      const sale = await loadSale();

      if (sale == null) {
        throw new Error();
      }

      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
        superadmin: sale.superadmin === true,
        fraktion: sale.fraktion,
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
// The cached sale has no avatar: a data URL avatar can exceed the quota.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

// A full or blocked storage must never break the login; the cache is optional.
function readCache(key: string): string | null {
  try {
    return getLocalStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

// Bumped whenever the cache is dropped, so a load that started before cannot
// write the previous user's data back.
let cacheGeneration = 0;

function writeCache(key: string, value: string, generation = cacheGeneration) {
  if (generation !== cacheGeneration) return;
  try {
    getLocalStorage()?.setItem(key, value);
  } catch {
    // Quota exceeded or storage blocked: carry on without cache.
  }
}

export async function getIsInitialized() {
  const cachedValue = readCache(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const generation = cacheGeneration;
  const { data, error } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  // A failed read says nothing about the setup: stay signed in, ask again later.
  if (error != null) {
    warnAuth("init_state not readable", error);
    return true;
  }
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    writeCache(IS_INITIALIZED_CACHE_KEY, "true", generation);
  }

  return isInitialized;
}

const getSale = async () => {
  if (saleRequest != null) {
    return saleRequest;
  }
  try {
    const cachedValue = readCache(CURRENT_SALE_CACHE_KEY);
    if (cachedValue != null) {
      const cached = JSON.parse(cachedValue);
      // Caches written before fraktion was selected lack it; refetch.
      if ("fraktion" in cached) {
        return cached;
      }
    }
  } catch {
    // Unreadable cache: load the sale instead.
  }
  return loadSale();
};

// Loads the sale at most once per page; a failed load is retried next call.
let saleRequest: ReturnType<typeof fetchSale> | null = null;

const loadSale = () => {
  if (saleRequest == null) {
    const request: ReturnType<typeof fetchSale> = fetchSale().then(
      (sale) => {
        if (sale == null && saleRequest === request) saleRequest = null;
        return sale;
      },
      (error) => {
        if (saleRequest === request) saleRequest = null;
        throw error;
      },
    );
    saleRequest = request;
  }
  return saleRequest;
};

const fetchSale = async () => {
  const generation = cacheGeneration;
  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select(
      "id, first_name, last_name, avatar, administrator, superadmin, fraktion",
    )
    .match({ user_id: dataSession?.session?.user.id })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  writeCache(
    CURRENT_SALE_CACHE_KEY,
    JSON.stringify({ ...dataSale, avatar: undefined }),
    generation,
  );
  return dataSale;
};

function clearCache(keys = [IS_INITIALIZED_CACHE_KEY, CURRENT_SALE_CACHE_KEY]) {
  cacheGeneration++;
  saleRequest = null;
  try {
    const storage = getLocalStorage();
    keys.forEach((key) => storage?.removeItem(key));
  } catch {
    // Storage blocked: nothing cached to clear.
  }
}

/** Drops the loaded sale after a sales update, so the identity shows the new name and avatar. */
export function invalidateSale() {
  clearCache([CURRENT_SALE_CACHE_KEY]);
}

const SESSION_RETRY_MS = 1500;

// Logs name and reason only: no token, no email.
function warnAuth(reason: string, error: unknown) {
  const name = error instanceof Error ? error.name : typeof error;
  console.warn(
    `[auth] ${name === "QuotaExceededError" ? "session not saved, storage full" : reason}`,
    name,
  );
}

async function readSession() {
  try {
    const { data, error } = await getSupabaseClient().auth.getSession();
    return { session: data.session, error };
  } catch (error) {
    // Lock timeouts and failed session writes (QuotaExceeded) are thrown.
    return { session: null, error };
  }
}

/** A session read that failed gets one more try after a short pause. */
async function readSessionWithRetry() {
  const first = await readSession();
  if (first.error == null) return first;
  warnAuth("getSession failed, retrying", first.error);
  await new Promise((resolve) => setTimeout(resolve, SESSION_RETRY_MS));
  const second = await readSession();
  if (second.error != null) warnAuth("getSession failed twice", second.error);
  return second;
}

const SIGNOUT_LOG_STORE = "RaStore.auth.signout_log";
const SIGNOUT_LOG_SIZE = 5;
const LIBRARY_TRIGGER = "library or other tab";

// What asked for the sign-out; if nothing set it, the library did it itself
// (refresh failed) or another tab signed out.
let signOutTrigger = LIBRARY_TRIGGER;

// Keeps the last sign-outs with their cause, so an unwanted logout can be
// traced. No token, no email.
function recordSignOut() {
  const trigger = signOutTrigger;
  signOutTrigger = LIBRARY_TRIGGER;
  console.warn("[auth] signed out", trigger);
  try {
    const log = JSON.parse(readCache(SIGNOUT_LOG_STORE) ?? "[]");
    log.push({ at: new Date().toISOString(), trigger });
    getLocalStorage()?.setItem(
      SIGNOUT_LOG_STORE,
      JSON.stringify(log.slice(-SIGNOUT_LOG_SIZE)),
    );
  } catch {
    // The log is optional: a full or blocked storage must not matter.
  }
}

let watching = false;

// Once per page: log every sign-out and ask the browser not to evict the
// stored session under storage pressure.
function watchSession() {
  if (watching) return;
  watching = true;
  getSupabaseClient().auth.onAuthStateChange((event) => {
    if (event === "SIGNED_OUT") recordSignOut();
  });
  navigator.storage?.persist?.().catch(() => {});
}

const isRedirect = (error: unknown) =>
  typeof error === "object" && error != null && "redirectTo" in error;

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  watchSession();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }

      // Fallback for SSO handleCallback or other flows
      return baseAuthProvider.login(params);
    },
    // Only this device signs out; `everywhere` ends the sessions on all devices.
    logout: async (params) => {
      clearCache();
      const everywhere = params?.everywhere === true;
      signOutTrigger = everywhere ? "user, all devices" : "user";
      const { error } = await getSupabaseClient().auth.signOut({
        scope: everywhere ? "global" : "local",
      });
      if (error) {
        throw error;
      }
    },
    // A refused request (403) is no expired login. A 401 signs out only when no
    // session is left after a refresh attempt.
    checkError: async (error) => {
      if (error == null || error.status === 403) return;
      try {
        await baseAuthProvider.checkError(error);
      } catch (rejection) {
        if (error.status !== 401) throw rejection;
        const { session, error: sessionError } = await readSessionWithRetry();
        if (session == null && sessionError == null) {
          signOutTrigger = "request answered 401, no session left";
          throw rejection;
        }
      }
    },
    checkAuth: async (params) => {
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }
      // Users are on the OAuth consent page, handled separately
      if (
        window.location.pathname === "/oauth/consent" ||
        window.location.hash.includes("#/oauth/consent")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        signOutTrigger = "setup not initialized";
        await getSupabaseClient().auth.signOut({ scope: "local" });
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      try {
        return await baseAuthProvider.checkAuth(params);
      } catch (error) {
        // Invite and recovery links redirect to the set-password page.
        if (isRedirect(error)) throw error;
      }
      // ra-supabase also signs out when reading the session merely failed;
      // only a clean "no session" signs out here.
      const { session, error } = await readSessionWithRetry();
      if (session == null && error == null) {
        return Promise.reject();
      }
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (!isInitialized) return false;

      // Get the current user
      const sale = await getSale();
      if (sale == null) return false;

      // Compute access rights from the sale role
      const role = getCrmRole(sale);
      return canAccess(role, params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
